import React from 'react';
import {CircularProgress} from '@mui/material';
import {Button, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import useDownload, {DownloadTarget} from '@/hooks/use-download/useDownload';

type DownloadButtonProps = DownloadTarget;

function DownloadButton({id, url, fileName}: DownloadButtonProps) {
  const {status, progress, download} = useDownload({id, url, fileName});

  const isDownloading = status === 'downloading';
  const isDone = status === 'done';
  const isError = status === 'error';
  const isDisabled = isDownloading || isDone || isError;

  const startIcon = (() => {
    if (isDownloading) {
      return (
        <CircularProgress
          size={16}
          variant="determinate"
          value={progress}
          color="inherit"
        />
      );
    }
    if (isDone) {
      return (
        <Icon
          name="Actions-Patch-Accept-Line"
          size="small"
          appearance="neutral"
          color="currentColor"
        />
      );
    }
    if (isError) {
      return (
        <Icon
          name="Features-Reject"
          size="small"
          appearance="neutral"
          color="currentColor"
        />
      );
    }
    return (
      <Icon
        name="Navigation-Download"
        size="small"
        appearance="neutral"
        color="currentColor"
      />
    );
  })();

  const label = (() => {
    if (isDownloading) {
      return translate('Downloading');
    }
    if (isDone) {
      return translate('Downloaded');
    }
    if (isError) {
      return translate('Failed');
    }
    return translate('Download');
  })();

  return (
    <Button
      appearance="neutral"
      mode="plain"
      size="small"
      startIcon={startIcon}
      disabled={isDisabled}
      onClick={download}
    >
      {label}
    </Button>
  );
}

export default DownloadButton;
